import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export interface UserClaim {
  claim_type: string;
  claim_value?: string;
  resource_type?: string;
  resource_id?: string;
}

export interface User {
  id: string;
  auth0_user_id: string;
  email: string;
  name?: string;
  picture?: string;
  role: string;
  is_active: boolean;
  tenant_id: string;
  created_at: string;
  updated_at?: string;
  claims?: UserClaim[];
}

export interface Tenant {
  id: string;
  name: string;
  company_name: string;
  domain_name: string;
  seats: number;
  document_quota: number;
  documents_used: number;
  slug: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  user_count?: number;
}

export interface Document {
  id: string;
  title?: string;
  description?: string;
  filename: string;
  original_filename: string;
  file_size?: number;
  mime_type?: string;
  document_type?: string;
  contract_type?: string;
  contract_category?: string;
  risk_level?: string;

  // Parties involved
  counterparty_name?: string;
  counterparty_type?: string;
  counterparty_contact_name?: string;
  counterparty_contact_email?: string;
  counterparty_contact_phone?: string;

  // Contract dates and lifecycle
  execution_date?: string;
  effective_date?: string;
  expiration_date?: string;
  notice_period_days?: number;

  // Renewal and termination
  auto_renewal?: boolean;
  renewal_term_months?: number;
  termination_clause?: string;

  // Financial terms
  contract_value?: string;
  currency?: string;
  payment_terms?: string;
  payment_frequency?: string;

  // Legal and compliance
  governing_law?: string;
  confidentiality_clause?: boolean;
  indemnification_clause?: boolean;
  limitation_of_liability?: boolean;
  intellectual_property_clause?: boolean;
  force_majeure_clause?: boolean;

  // Business information
  department?: string;
  business_unit?: string;
  contract_owner_id?: string;

  // Status and workflow
  approval_status?: string;
  contract_status?: string;
  priority?: string;

  // Additional metadata
  contract_number?: string;
  external_contract_id?: string;
  regulatory_requirements?: string;
  special_terms?: string;

  processing_status: string;
  uploaded_by_id: string;
  vendor_id?: string;
  created_at: string;
  updated_at?: string;
  ai_summary?: string;
  ai_analysis?: string;
  uploaded_by?: User;
  vendor?: Vendor;
  parent_relationships?: DocumentRelationship[];
  child_relationships?: DocumentRelationship[];
}

export interface Vendor {
  id: string;
  name: string;
  vendor_type?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  industry?: string;
  company_size?: string;
  website?: string;
  tax_id?: string;
  risk_level?: string;
  compliance_status?: string;
  description?: string;
  tags?: string;
  ai_confidence?: number;
  ai_detection_notes?: string;
  is_active: boolean;
  tenant_id: string;
  created_at: string;
  updated_at?: string;
  document_count?: number;
}

export interface DocumentRelationship {
  id: string;
  parent_document_id: string;
  child_document_id: string;
  relationship_type: string;
  description?: string;
  confidence_score?: number;
  source_text?: string;
  extraction_method?: string;
  extraction_note?: string;
  validation_status: string;
  validated_by?: string;
  validated_at?: string;
  created_at: string;
  updated_at?: string;
  parent_document?: Document;
  child_document?: Document;
}

export interface CreateVendorData {
  name: string;
  vendor_type?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  industry?: string;
  company_size?: string;
  website?: string;
  tax_id?: string;
  risk_level?: string;
  compliance_status?: string;
  description?: string;
  tags?: string;
  tenant_id: string;
}

export interface UpdateVendorData {
  name?: string;
  vendor_type?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  industry?: string;
  company_size?: string;
  website?: string;
  tax_id?: string;
  risk_level?: string;
  compliance_status?: string;
  description?: string;
  tags?: string;
  is_active?: boolean;
}

export interface RenameVendorData {
  id: string;
  new_name: string;
}

export interface CreateRelationshipData {
  parent_document_id: string;
  child_document_id: string;
  relationship_type: string;
  description?: string;
  confidence_score?: number;
  source_text?: string;
  extraction_method?: string;
  extraction_note?: string;
}

export interface UpdateRelationshipData {
  relationship_type?: string;
  description?: string;
  confidence_score?: number;
  source_text?: string;
  extraction_method?: string;
  extraction_note?: string;
  validation_status?: string;
}

export interface VendorQueryParams {
  skip?: number;
  limit?: number;
  search?: string;
  vendor_type?: string;
  is_active?: boolean;
  include_system?: boolean;
  include_inactive?: boolean;
}

export interface RelationshipQueryParams {
  skip?: number;
  limit?: number;
  parent_document_id?: string;
  child_document_id?: string;
  relationship_type?: string;
}

export interface DocumentSummary {
  id: string;
  title?: string;
  original_filename: string;
  document_type?: string;
  contract_type?: string;
  contract_status?: string;
  processing_status: string;
  created_at: string;
  updated_at?: string;
}

export interface RelatedDocumentsResponse {
  governed_by: RelatedDocumentInfo[];
  governs: RelatedDocumentInfo[];
  all_relationships: RelatedDocumentInfo[];
}

export interface RelatedDocumentInfo {
  relationship_id: string;
  relationship_type: string;
  description?: string;
  confidence_score?: number;
  document: DocumentSummary;
}

export interface RelationshipGraphResponse {
  nodes: GraphNode[];
  edges: GraphEdge[];
  root_document_id: string;
}

export interface GraphNode {
  id: string;
  label: string;
  type?: string;
  contract_type?: string;
  processing_status: string;
  is_root: boolean;
}

export interface GraphEdge {
  id: string;
  source: string;
  target: string;
  label: string;
  description?: string;
  confidence_score?: number;
}

export interface ListResponse<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  has_next: boolean;
}

export interface SearchResult {
  document: Document;
  relevance_score?: number;
  match_type: string;
  matched_text?: string;
}

export interface SearchResponse {
  results: SearchResult[];
  total: number;
  query: string;
  processed_query: any;
  page: number;
  size: number;
  has_next: boolean;
}

export interface UserInvite {
  email: string;
  name?: string;
  role: "admin" | "user" | "viewer";
}

export interface BulkDocumentUploadResult {
  total_files: number;
  successful_uploads: number;
  failed_uploads: number;
  created_vendors: string[];
  documents: Document[];
  errors: string[];
}

export interface UserListResponse {
  items: User[];
  total: number;
  page: number;
  size: number;
  has_next: boolean;
}

// Global variable to store Auth0 instance
let auth0Instance: any = null;

// Function to set Auth0 instance (called from React components)
export const setAuth0Instance = (instance: any) => {
  auth0Instance = instance;
};

// Log the API configuration
console.log("API Configuration:", {
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  baseUrl: process.env.REACT_APP_API_URL || "/api/v1",
});

// Custom base query that gets Auth0 token
const baseQueryWithAuth = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL || "/api/v1",
  prepareHeaders: async (headers, { getState, endpoint }) => {
    // First try to get token from Redux state
    const state = getState() as any;
    let token = state.auth?.token;

    // If no token in Redux and we have Auth0 instance, try to get token from Auth0
    if (!token && auth0Instance && auth0Instance.isAuthenticated) {
      try {
        token = await auth0Instance.getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: "openid profile email",
          },
        });
        console.log(
          "Retrieved token directly from Auth0:",
          token ? "TOKEN_PRESENT" : "NO_TOKEN",
        );
      } catch (error) {
        console.error("Error getting token from Auth0:", error);
        // If we can't get token, this request should fail gracefully
        return headers;
      }
    }

    console.log(`API call to ${endpoint}:`, {
      baseUrl: process.env.REACT_APP_API_URL || "/api/v1",
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 20)}...` : "NO_TOKEN",
      isAuthenticated:
        state.auth?.isAuthenticated ||
        (auth0Instance && auth0Instance.isAuthenticated),
      auth0Available: !!auth0Instance,
    });

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    return headers;
  },
});

// Define the API slice
export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["User", "Tenant", "Document", "Vendor", "DocumentRelationship"],
  endpoints: (builder) => ({
    // Chat endpoints
    startDocumentChat: builder.mutation<
      { status: string; message: string },
      { documentId: string }
    >({
      query: ({ documentId }) => ({
        url: `/chat/start/document/${documentId}`,
        method: "POST",
      }),
    }),
    startVendorChat: builder.mutation<
      { status: string; message: string },
      { vendorId: string }
    >({
      query: ({ vendorId }) => ({
        url: `/chat/start/vendor/${vendorId}`,
        method: "POST",
      }),
    }),
    sendDocumentChatMessage: builder.mutation<
      { reply: string },
      {
        documentId: string;
        message: string;
        history?: { role: string; content: string }[];
      }
    >({
      query: ({ documentId, message, history }) => ({
        url: `/chat/document/${documentId}/message`,
        method: "POST",
        body: { message, history },
      }),
    }),
    sendVendorChatMessage: builder.mutation<
      { reply: string },
      {
        vendorId: string;
        message: string;
        history?: { role: string; content: string }[];
      }
    >({
      query: ({ vendorId, message, history }) => ({
        url: `/chat/vendor/${vendorId}/message`,
        method: "POST",
        body: { message, history },
      }),
    }),
    // Auth endpoints
    getCurrentUser: builder.query<User, void>({
      query: () => "/auth/me",
      providesTags: ["User"],
    }),

    // Tenant endpoints
    getTenants: builder.query<
      ListResponse<Tenant>,
      { skip?: number; limit?: number }
    >({
      query: ({ skip = 0, limit = 50 }) =>
        `/tenants?skip=${skip}&limit=${limit}`,
      providesTags: ["Tenant"],
    }),

    getTenant: builder.query<Tenant, string>({
      query: (id) => `/tenants/${id}`,
      providesTags: ["Tenant"],
    }),

    createTenant: builder.mutation<
      Tenant,
      {
        name: string;
        company_name: string;
        domain_name: string;
        seats: number;
        document_quota: number;
        slug: string;
        description?: string;
      }
    >({
      query: (data) => ({
        url: "/tenants",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Tenant"],
    }),

    updateTenant: builder.mutation<
      Tenant,
      {
        id: string;
        name?: string;
        company_name?: string;
        domain_name?: string;
        seats?: number;
        document_quota?: number;
        description?: string;
        is_active?: boolean;
      }
    >({
      query: ({ id, ...data }) => ({
        url: `/tenants/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Tenant"],
    }),

    deleteTenant: builder.mutation<void, string>({
      query: (id) => ({
        url: `/tenants/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Tenant"],
    }),

    // Tenant user management endpoints
    getTenantUsers: builder.query<
      UserListResponse,
      {
        tenantId: string;
        skip?: number;
        limit?: number;
      }
    >({
      query: ({ tenantId, skip = 0, limit = 50 }) =>
        `/tenants/${tenantId}/users?skip=${skip}&limit=${limit}`,
      providesTags: ["User"],
    }),

    inviteUserToTenant: builder.mutation<
      { message: string },
      {
        tenantId: string;
        userData: UserInvite;
      }
    >({
      query: ({ tenantId, userData }) => ({
        url: `/tenants/${tenantId}/users/invite`,
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["User", "Tenant"],
    }),

    deleteUserFromTenant: builder.mutation<
      void,
      {
        tenantId: string;
        userId: string;
      }
    >({
      query: ({ tenantId, userId }) => ({
        url: `/tenants/${tenantId}/users/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["User", "Tenant"],
    }),

    // User claims management endpoints
    assignUserClaim: builder.mutation<
      { message: string },
      {
        tenantId: string;
        userId: string;
        claimType: "tenant_admin" | "tenant_member";
      }
    >({
      query: ({ tenantId, userId, claimType }) => ({
        url: `/tenants/${tenantId}/users/${userId}/claims`,
        method: "POST",
        body: { claim_type: claimType },
      }),
      invalidatesTags: ["User"],
    }),

    removeUserClaim: builder.mutation<
      void,
      {
        tenantId: string;
        userId: string;
        claimType: "tenant_admin" | "tenant_member";
      }
    >({
      query: ({ tenantId, userId, claimType }) => ({
        url: `/tenants/${tenantId}/users/${userId}/claims/${claimType}`,
        method: "DELETE",
      }),
      invalidatesTags: ["User"],
    }),

    getUserClaims: builder.query<
      any,
      {
        tenantId: string;
        userId: string;
      }
    >({
      query: ({ tenantId, userId }) =>
        `/tenants/${tenantId}/users/${userId}/claims`,
      providesTags: ["User"],
    }),

    // Document endpoints
    getDocuments: builder.query<
      ListResponse<Document>,
      {
        vendor_id?: string;
        skip?: number;
        limit?: number;
        status_filter?: string;
        document_type?: string;
      }
    >({
      query: ({
        vendor_id,
        skip = 0,
        limit = 50,
        status_filter,
        document_type,
      }) => {
        const params = new URLSearchParams({
          skip: skip.toString(),
          limit: limit.toString(),
        });
        if (vendor_id) params.append("vendor_id", vendor_id);
        if (status_filter) params.append("status_filter", status_filter);
        if (document_type) params.append("document_type", document_type);
        return `/documents?${params}`;
      },
      providesTags: ["Document"],
    }),

    searchDocuments: builder.query<
      SearchResponse,
      {
        query: string;
        skip?: number;
        limit?: number;
        vendor_id?: string;
        document_type?: string;
        status_filter?: string;
      }
    >({
      query: ({
        query,
        skip = 0,
        limit = 50,
        vendor_id,
        document_type,
        status_filter,
      }) => {
        const params = new URLSearchParams({
          query: query,
          skip: skip.toString(),
          limit: limit.toString(),
        });
        if (vendor_id) params.append("vendor_id", vendor_id);
        if (document_type) params.append("document_type", document_type);
        if (status_filter) params.append("status_filter", status_filter);
        return `/documents/search?${params}`;
      },
      providesTags: ["Document"],
    }),

    getSearchSuggestions: builder.query<
      {
        suggestions: Array<{
          id: string;
          title: string;
          original_filename: string;
        }>;
      },
      {
        query: string;
        limit?: number;
      }
    >({
      query: ({ query, limit = 5 }) => {
        const params = new URLSearchParams({
          query: query,
          limit: limit.toString(),
        });
        return `/documents/search/suggestions?${params}`;
      },
      providesTags: ["Document"],
    }),

    // exportDocuments: handled via component-level fetch to ensure Blob handling

    getDocument: builder.query<Document, string>({
      query: (id) => `/documents/${id}`,
      providesTags: ["Document"],
    }),

    uploadDocument: builder.mutation<Document, FormData>({
      query: (formData) => ({
        url: "/documents/upload",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Document", "Vendor"],
    }),

    bulkUploadDocuments: builder.mutation<Document[], FormData>({
      query: (formData) => ({
        url: "/documents/bulk-upload",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Document", "Vendor"],
    }),

    bulkUploadDocumentsVendorBased: builder.mutation<
      BulkDocumentUploadResult,
      FormData
    >({
      query: (formData) => ({
        url: "/documents/bulk-upload-vendor",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Document", "Vendor"],
    }),

    updateDocument: builder.mutation<
      Document,
      {
        id: string;
        title?: string;
        description?: string;
        document_type?: string;
      }
    >({
      query: ({ id, ...data }) => ({
        url: `/documents/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Document"],
    }),

    deleteDocument: builder.mutation<void, string>({
      query: (id) => ({
        url: `/documents/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Document"],
    }),

    bulkDeleteDocuments: builder.mutation<
      {
        deleted_ids: string[];
        failed_ids: string[];
        deleted_count: number;
        error_messages: string[];
      },
      { ids: string[] }
    >({
      query: (body) => ({
        url: "/documents/bulk-delete",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Document"],
    }),

    analyzeDocument: builder.mutation<
      any,
      { id: string; analysis_type: string }
    >({
      query: ({ id, analysis_type }) => ({
        url: `/documents/${id}/analyze`,
        method: "POST",
        body: { document_id: id, analysis_type },
      }),
    }),

    getDocumentContent: builder.query<any, string>({
      query: (id) => `/documents/${id}/content`,
    }),

    // Key term validation
    validateKeyTerm: builder.mutation<
      { term: any; message: string },
      {
        documentId: string;
        termId: string;
        validation_status: "verified" | "rejected" | "manual" | "unverified";
        updated_value?: string;
        validator_notes?: string;
      }
    >({
      query: ({
        documentId,
        termId,
        validation_status,
        updated_value,
        validator_notes,
      }) => ({
        url: `/documents/${documentId}/key-terms/${termId}/validate`,
        method: "POST",
        body: { validation_status, updated_value, validator_notes },
      }),
      invalidatesTags: ["Document"],
    }),

    // Vendor endpoints
    getVendors: builder.query<
      { items: Vendor[]; total: number },
      VendorQueryParams
    >({
      query: ({
        skip = 0,
        limit = 50,
        search,
        vendor_type,
        is_active,
        include_system,
        include_inactive,
      }) => ({
        url: "/vendors",
        params: {
          skip,
          limit,
          search,
          vendor_type,
          is_active,
          include_system,
          include_inactive,
        },
      }),
      providesTags: ["Vendor"],
    }),

    getVendor: builder.query<Vendor, string>({
      query: (id) => `/vendors/${id}`,
      providesTags: ["Vendor"],
    }),

    createVendor: builder.mutation<Vendor, CreateVendorData>({
      query: (data) => ({
        url: "/vendors",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Vendor"],
    }),

    updateVendor: builder.mutation<
      Vendor,
      { id: string; data: UpdateVendorData }
    >({
      query: ({ id, data }) => ({
        url: `/vendors/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Vendor"],
    }),

    renameVendor: builder.mutation<Vendor, RenameVendorData>({
      query: ({ id, new_name }) => ({
        url: `/vendors/${id}/rename`,
        method: "POST",
        body: { new_name },
      }),
      invalidatesTags: ["Vendor"],
    }),

    deleteVendor: builder.mutation<void, string>({
      query: (id) => ({
        url: `/vendors/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Vendor"],
    }),

    getVendorDocuments: builder.query<DocumentSummary[], string>({
      query: (vendorId) => `/vendors/${vendorId}/documents`,
      providesTags: ["Document"],
    }),

    // Document relationship endpoints
    getDocumentRelationships: builder.query<
      { items: DocumentRelationship[]; total: number },
      RelationshipQueryParams
    >({
      query: ({
        skip = 0,
        limit = 50,
        parent_document_id,
        child_document_id,
        relationship_type,
      }) => ({
        url: "/document-relationships",
        params: {
          skip,
          limit,
          parent_document_id,
          child_document_id,
          relationship_type,
        },
      }),
      providesTags: ["DocumentRelationship"],
    }),

    getDocumentRelationship: builder.query<DocumentRelationship, string>({
      query: (id) => `/document-relationships/${id}`,
      providesTags: ["DocumentRelationship"],
    }),

    createDocumentRelationship: builder.mutation<
      DocumentRelationship,
      CreateRelationshipData
    >({
      query: (data) => ({
        url: "/document-relationships",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["DocumentRelationship"],
    }),

    updateDocumentRelationship: builder.mutation<
      DocumentRelationship,
      { id: string; data: UpdateRelationshipData }
    >({
      query: ({ id, data }) => ({
        url: `/document-relationships/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["DocumentRelationship"],
    }),

    deleteDocumentRelationship: builder.mutation<void, string>({
      query: (id) => ({
        url: `/document-relationships/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["DocumentRelationship"],
    }),

    getDocumentRelatedDocuments: builder.query<
      RelatedDocumentsResponse,
      string
    >({
      query: (documentId) =>
        `/document-relationships/document/${documentId}/related`,
      providesTags: ["DocumentRelationship"],
    }),

    getDocumentRelationshipGraph: builder.query<
      RelationshipGraphResponse,
      { documentId: string; maxDepth?: number }
    >({
      query: ({ documentId, maxDepth = 3 }) => ({
        url: `/document-relationships/document/${documentId}/graph`,
        params: { max_depth: maxDepth },
      }),
      providesTags: ["DocumentRelationship"],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetCurrentUserQuery,
  useGetTenantsQuery,
  useGetTenantQuery,
  useCreateTenantMutation,
  useUpdateTenantMutation,
  useDeleteTenantMutation,
  useGetTenantUsersQuery,
  useInviteUserToTenantMutation,
  useDeleteUserFromTenantMutation,
  useAssignUserClaimMutation,
  useRemoveUserClaimMutation,
  useGetUserClaimsQuery,
  useGetDocumentsQuery,
  useSearchDocumentsQuery,
  useGetSearchSuggestionsQuery,
  useGetDocumentQuery,
  useUploadDocumentMutation,
  useBulkUploadDocumentsMutation,
  useBulkUploadDocumentsVendorBasedMutation,
  useUpdateDocumentMutation,
  useDeleteDocumentMutation,
  useBulkDeleteDocumentsMutation,
  useAnalyzeDocumentMutation,
  useGetDocumentContentQuery,
  useValidateKeyTermMutation,
  useGetVendorsQuery,
  useGetVendorQuery,
  useCreateVendorMutation,
  useUpdateVendorMutation,
  useRenameVendorMutation,
  useDeleteVendorMutation,
  useGetVendorDocumentsQuery,
  useGetDocumentRelationshipsQuery,
  useGetDocumentRelationshipQuery,
  useCreateDocumentRelationshipMutation,
  useUpdateDocumentRelationshipMutation,
  useDeleteDocumentRelationshipMutation,
  useGetDocumentRelatedDocumentsQuery,
  useGetDocumentRelationshipGraphQuery,
  useStartDocumentChatMutation,
  useStartVendorChatMutation,
  useSendDocumentChatMessageMutation,
  useSendVendorChatMessageMutation,
} = apiSlice;
